import React, { useContext } from "react";
import IRMA from "@models/IRMA";
import IRMA_Line_Item from "@models/IRMA_Line_Item";
import IRMA_Line_Item_Inspection from "@models/IRMA_Line_Item_Inspection";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTable from "@components/shared/OwTable/OwTable";
import OwButton from "@components/shared/OwButton/OwButton";
import OwPill from "@components/shared/OwPill/OwPill";
import IOwTableCols from "@models/IOwTableCols";
import RmaStatusEnum from "@models/RmaStatusEnum";
import UIService from "@services/UIService";
import "./RmaInspectionsSection.scss";
import UserService from "@services/UserService";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";

type Props = {
    rma: IRMA;
    onEdit?: (lineItem: IRMA_Line_Item, inspection: IRMA_Line_Item_Inspection) => void;
    onDelete?: (lineItem: IRMA_Line_Item, inspection: IRMA_Line_Item_Inspection) => void;
    loading?: boolean;
};

export const RmaInspectionsSection = (props: Props) => {
    const { rma, loading } = props;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const canEditInspections = rma.rma_status_id === RmaStatusEnum.RECEIVED;

    const formatDate = (date: Date | string | null | undefined): string => {
        if (!date) return "-";
        return UserService.parseDate(new Date(date));
    };

    const formatCurrency = (amount: number | null | undefined): string => {
        if (!amount) return "-";
        return `$${amount.toFixed(2)}`;
    };

    const getColumns = (lineItem: IRMA_Line_Item): IOwTableCols[] => {
        const baseColumns: IOwTableCols[] = [
            {
                title: "condition",
                valueProp: "Condition_On_Receipt_Type.name",
                className: "w-25",
                renderFnEmitsRow: true,
                renderFn: (row: IRMA_Line_Item_Inspection) => (
                    <span>
                        <OwLabel title={row.Condition_On_Receipt_Type?.name} />
                    </span>
                ),
            },
            {
                title: "quantity",
                valueProp: "quantity",
                className: "w-10",
                renderFnEmitsRow: true,
                renderFn: (row: IRMA_Line_Item_Inspection) => <span>{row.quantity}</span>,
            },
            {
                title: "restockable",
                valueProp: "restockable",
                className: "w-10",
                renderFnEmitsRow: true,
                renderFn: (row: IRMA_Line_Item_Inspection) => (
                    <OwPill className={`px-2 ${row.restockable ? "bg-success" : "bg-danger"}`} title={row.restockable ? "yes" : "no"} />
                ),
            },
            {
                title: "warehouse",
                valueProp: "restock_warehouse.name",
                className: "w-25",
                renderFnEmitsRow: true,
                renderFn: (row: IRMA_Line_Item_Inspection) => <span>{row.restock_warehouse?.name || "-"}</span>,
                notVisible: !supplierModel.has_warehouses,
            },
            {
                title: "refund_amount",
                valueProp: "refund_amount",
                className: "w-10",
                renderFnEmitsRow: true,
                renderFn: (row: IRMA_Line_Item_Inspection) => <span>{formatCurrency(row.refund_amount)}</span>,
            },
            {
                title: "inspected_by",
                valueProp: "inspected_by_user.name",
                className: "w-25",
                renderFnEmitsRow: true,
                renderFn: (row: IRMA_Line_Item_Inspection) => (
                    <div>
                        <div>{row.inspected_by_user?.name || "-"}</div>
                        <div className="text-muted font-small">{formatDate(row.inspected_at)}</div>
                    </div>
                ),
            },
            {
                title: "notes",
                valueProp: "inspection_notes",
                className: "w-25",
                renderFnEmitsRow: true,
                renderFn: (row: IRMA_Line_Item_Inspection) => <span className="text-muted">{row.inspection_notes || "-"}</span>,
            },
        ];

        if (canEditInspections && props.onEdit && props.onDelete) {
            baseColumns.push({
                title: "actions",
                valueProp: "actions",
                className: "w-10",
                renderFnEmitsRow: true,
                renderFn: (row: IRMA_Line_Item_Inspection) => (
                    <div className="d-flex gap-2">
                        <OwButton title="edit" className="btn-secondary" fn={() => props.onEdit!(lineItem, row)} active={loading} />
                        <OwButton title="delete" className="btn-danger" fn={() => props.onDelete!(lineItem, row)} active={loading} />
                    </div>
                ),
            });
        }

        return baseColumns;
    };

    const lineItems = rma.RMA_Line_Items || [];
    const lineItemsWithInspections = lineItems.filter(
        (item) => item.RMA_Line_Item_Inspections && item.RMA_Line_Item_Inspections.length > 0
    );

    // Calculate summary statistics
    const totalReceived = lineItems.reduce((sum, item) => sum + (item.qty_received || 0), 0);
    const totalInspected = lineItems.reduce((sum, item) => {
        const inspections = item.RMA_Line_Item_Inspections || [];
        return sum + inspections.reduce((iSum, insp) => iSum + insp.quantity, 0);
    }, 0);
    const totalRefund = lineItems.reduce((sum, item) => {
        const inspections = item.RMA_Line_Item_Inspections || [];
        return sum + inspections.reduce((iSum, insp) => iSum + (insp.refund_amount || 0), 0);
    }, 0);

    return (
        <div className="RmaInspectionsSection mt-5">
            <h4 className="mb-3">
                <OwLabel title="rma_inspections" isBold={true} />
            </h4>

            {/* Summary statistics */}
            <div className="row mb-3">
                <div className="col-md-3">
                    <OwLabel title="total_received" isBold={true} />
                    <div className="h5">{totalReceived}</div>
                </div>
                <div className="col-md-3">
                    <OwLabel title="total_inspected" isBold={true} />
                    <div className="h5">{totalInspected}</div>
                </div>
                <div className="col-md-3">
                    <OwLabel title="total_refund" isBold={true} />
                    <div className="h5">{formatCurrency(totalRefund)}</div>
                </div>
                <div className="col-md-3">
                    <OwLabel title="inspection_progress" isBold={true} />
                    <div className="h5">{totalReceived > 0 ? Math.round((totalInspected / totalReceived) * 100) : 0}%</div>
                </div>
            </div>

            {/* Inspections grouped by line item */}
            {lineItemsWithInspections.length > 0 ? (
                lineItemsWithInspections.map((lineItem, index) => (
                    <div key={lineItem.id} className="mb-4">
                        <h5 className="mb-2">
                            {lineItem.sku} - {lineItem.catalog_item_name}
                        </h5>
                        <OwTable
                            userModel={userModel}
                            supplierModel={supplierModel}
                            dataSet={lineItem.RMA_Line_Item_Inspections || []}
                            columns={getColumns(lineItem)}
                            dataFetch={false}
                            initialData={false}
                        />
                    </div>
                ))
            ) : (
                <div className="text-muted">
                    <OwLabel title="no_inspections_recorded" />
                </div>
            )}
        </div>
    );
};
